// src/features/dispensers/components/DispensersPage.tsx
//
// «Dispensadores» — route /dispensers, Dirección. Keys delivered, share of
// delivery attempts that ended with a key (P05: a failed delivery is an
// attempt without a key, any cause), dispensers with activity out of those
// registered, failed deliveries, keys and failures per day, the
// per-installation ranking and the errors by type with the failures each one
// caused. Scope and period are the shared ones of the other Dirección screens.
import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  VpnKeyRounded as KeysIcon,
  TaskAltRounded as SuccessIcon,
  DevicesRounded as DispensersIcon,
  ReportProblemOutlined as FailedIcon,
} from '@mui/icons-material';
import { useDataProvider, useTranslate } from 'react-admin';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { HotelScopeSelector } from '@/components/common/HotelScopeSelector';
import { PeriodPicker } from '@/components/common/PeriodPicker';
import { EmptyState } from '@/components/common/EmptyState';
import { KpiHero } from '@/components/common/KpiHero';
import { WidgetCard } from '@/components/common/WidgetCard';
import { ResponsiveMetricsTable, type MetricsColumn } from '@/components/common/ResponsiveMetricsTable';
import { StackedBarsChart } from '@/components/common/analytics/StackedBarsChart';
import { useAnalyticsPeriod } from '@/providers/analyticsPeriodProvider';
import { useHotelScope } from '@/hooks/useHotelScope';
import { useAnalyticsFetch } from '@/hooks/useAnalyticsFetch';
import { getChannelHex } from '@/design/registry/channelRegistry';
import { DASH, formatCount, formatPct } from '@/lib/analyticsFormat';
import { errorTypeRows, hasDispensersInScope, measuredSuccessRate, type ErrorTypeRow } from '@/lib/dispenserView';
import type {
  DispenserByHotelResponse,
  DispenserDailyTrendResponse,
  DispenserHotelStats,
  DispenserQuery,
  DispenserSummary,
} from '@/types/dispensers';

interface DispenserReport {
  summary: DispenserSummary;
  byHotel: DispenserByHotelResponse;
  dailyTrend: DispenserDailyTrendResponse;
}

const t = (key: string) => `resources.dispensers.${key}`;

export const DispensersPage: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const dataProvider = useDataProvider() as any;
  const scope = useHotelScope();
  const [period, setPeriod] = useAnalyticsPeriod();
  const dateLocale = translate('common.locale') === 'en' ? enUS : es;

  // One report, three requests: a figure is never shown next to another that failed to load.
  const { data, loading, error, refresh } = useAnalyticsFetch<DispenserReport>(
    async () => {
      const query: DispenserQuery = { ...scope.scopeParams, dateFrom: period.from, dateTo: period.to };
      const [summary, byHotel, dailyTrend] = await Promise.all([
        dataProvider.getDispenserSummary(query),
        dataProvider.getDispenserByHotel(query),
        dataProvider.getDispenserDailyTrend(query),
      ]);
      return { data: { summary: summary.data, byHotel: byHotel.data, dailyTrend: dailyTrend.data } };
    },
    [dataProvider, scope.scopeParams, period.from, period.to]
  );

  const summary = data?.summary;
  const keycards = summary?.keycards;
  const successRate = keycards ? measuredSuccessRate(keycards.successful, keycards.failed, keycards.successRate) : null;
  const noDispensers = !loading && !error && data != null && !hasDispensersInScope(summary);

  const byDay = useMemo(
    () =>
      (data?.dailyTrend.trend ?? []).map((d) => {
        const day = new Date(`${d.date}T00:00:00`);
        return {
          date: Number.isNaN(day.getTime()) ? d.date : format(day, 'd MMM', { locale: dateLocale }),
          keysDispensed: d.keysDispensed,
          errors: d.errors,
        };
      }),
    [data, dateLocale]
  );

  const hotelColumns: MetricsColumn<DispenserHotelStats>[] = [
    { key: 'hotel', label: translate(t('byHotel.columns.hotel')), render: (row) => row.hotelName || row.hotelCode },
    { key: 'keysDispensed', label: translate(t('byHotel.columns.keysDispensed')), align: 'right', render: (row) => formatCount(row.keysDispensed) },
    { key: 'errors', label: translate(t('byHotel.columns.errors')), align: 'right', render: (row) => formatCount(row.errors) },
    {
      key: 'successRate',
      label: translate(t('byHotel.columns.successRate')),
      align: 'right',
      render: (row) => formatPct(measuredSuccessRate(row.keysDispensed, row.errors, row.successRate)),
    },
    { key: 'dispenserCount', label: translate(t('byHotel.columns.dispenserCount')), align: 'right', render: (row) => formatCount(row.dispenserCount) },
  ];

  const errorColumns: MetricsColumn<ErrorTypeRow>[] = [
    { key: 'type', label: translate(t('errorTypes.columns.type')), render: (row) => translate(t(`errorCodes.${row.code}`), { _: row.code }) },
    { key: 'count', label: translate(t('errorTypes.columns.count')), align: 'right', render: (row) => formatCount(row.count) },
    { key: 'failed', label: translate(t('errorTypes.columns.failed')), align: 'right', render: (row) => formatCount(row.failed) },
  ];

  const hotelRows = data?.byHotel.hotels ?? [];
  const errorRows = errorTypeRows(summary?.errors.byCode, summary?.keycards.failedByCause);
  const emptyLine = (key: string) => (
    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
      {translate(key)}
    </Typography>
  );

  return (
    <Box>
      <SectionHeader manualScreen="dispensers"
        left={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <HotelScopeSelector scope={scope} />
            <PeriodPicker value={period} onChange={setPeriod} showCompare={false} />
          </Box>
        }
        subtitle={translate(t('subtitle'))}
        helpConfig={{
          titleKey: t('help.title'),
          descriptionKey: t('help.description'),
          featureKeys: [t('help.features.success'), t('help.features.active')],
          tipKeys: [t('help.tips.errors')],
        }}
      />

      {error && <FetchErrorBanner onRetry={refresh} />}

      {/* A failed load shows the banner alone: no tile or table may read as "nothing
          happened", nor keep the figures of the previous scope or period. */}
      {error || (!loading && !data) ? null : noDispensers ? (
        <EmptyState
          icon={<DispensersIcon />}
          message={translate(t('noDispensers.title'))}
          description={translate(t('noDispensers.description'))}
          minHeight={220}
        />
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2, mb: 2.5 }}>
            <KpiHero
              label={translate(t('tiles.keysDelivered'))}
              value={formatCount(keycards?.totalIssued)}
              sublabel={translate(t('tiles.keysDeliveredDetail'))}
              icon={<KeysIcon />}
              loading={loading}
            />
            <KpiHero
              label={translate(t('tiles.successRate'))}
              value={!keycards ? DASH : successRate == null ? translate(t('tiles.noAttempts')) : formatPct(successRate)}
              empty={keycards != null && successRate == null}
              sublabel={
                keycards && successRate != null
                  ? translate(t('tiles.successRateDetail'), {
                      delivered: formatCount(keycards.successful),
                      attempts: formatCount(keycards.attempts),
                    })
                  : undefined
              }
              icon={<SuccessIcon />}
              loading={loading}
            />
            <KpiHero
              label={translate(t('tiles.activeDispensers'))}
              value={summary ? `${formatCount(summary.dispensers.active)}/${formatCount(summary.dispensers.total)}` : DASH}
              sublabel={translate(t('tiles.activeDispensersDetail'))}
              icon={<DispensersIcon />}
              loading={loading}
            />
            <KpiHero
              label={translate(t('tiles.failed'))}
              value={formatCount(keycards?.failed)}
              sublabel={translate(t('tiles.failedDetail'))}
              icon={<FailedIcon />}
              loading={loading}
            />
          </Box>

          <WidgetCard title={translate(t('byDay.title'))} sx={{ mb: 2.5 }}>
            <StackedBarsChart
              data={byDay}
              xKey="date"
              stacked={false}
              series={[
                { key: 'keysDispensed', labelKey: t('byDay.series.keysDispensed'), color: getChannelHex('Dispenser') },
                { key: 'errors', labelKey: t('byDay.series.errors'), color: theme.palette.error.main },
              ]}
            />
          </WidgetCard>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, gap: 2.5, alignItems: 'start' }}>
            <WidgetCard title={translate(t('byHotel.title'))} description={translate(t('byHotel.description'))}>
              {!loading && hotelRows.length === 0 ? (
                emptyLine(t('byHotel.empty'))
              ) : (
                <ResponsiveMetricsTable<DispenserHotelStats> columns={hotelColumns} rows={hotelRows} getRowKey={(row) => row.hotelId} loading={loading} />
              )}
            </WidgetCard>

            <WidgetCard title={translate(t('errorTypes.title'))} description={translate(t('errorTypes.description'))}>
              {!loading && errorRows.length === 0 ? (
                emptyLine(t('errorTypes.empty'))
              ) : (
                <ResponsiveMetricsTable<ErrorTypeRow> columns={errorColumns} rows={errorRows} getRowKey={(row) => row.code} loading={loading} />
              )}
            </WidgetCard>
          </Box>
        </>
      )}
    </Box>
  );
};

export default DispensersPage;
